import { useCallback, useEffect, useRef, useState } from 'react';
import { E2E_RUN_BRANCH_PATTERN } from 'shared-models/browser';
import { e2eRunnerService } from '../../services/e2e-runner/e2e-runner.service';
import type { E2eRunReportView, E2eRunTier, E2eRunnerStatusView } from '../../services/e2e-runner/e2e-runner.service';

export const E2E_BRANCH_STORAGE_KEY = 'e2e-runner-branch';
export const E2E_POLL_INTERVAL_MS = 10_000;
export const E2E_POLL_BACKOFF_MS = 60_000;
const MAX_FAILURES_BEFORE_BACKOFF = 3;

export type E2eRunnerController = {
  status: E2eRunnerStatusView | null;
  isBusy: boolean;
  error: string | null;
  statusError: boolean;
  pendingTier: E2eRunTier | null;
  branchDraft: string;
  isBranchDraftValid: boolean;
  areReportsOpen: boolean;
  runs: E2eRunReportView[];
  isRunsLoading: boolean;
  runsError: string | null;
  toggleReports: () => void;
  setBranchDraft: (value: string) => void;
  requestLaunch: (tier: E2eRunTier) => void;
  cancelLaunch: () => void;
  confirmLaunch: () => void;
  stopRun: () => void;
};

const readStoredBranch = (): string => {
  try {
    return window.localStorage.getItem(E2E_BRANCH_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};

const storeBranch = (branch: string): void => {
  try {
    window.localStorage.setItem(E2E_BRANCH_STORAGE_KEY, branch);
  } catch {
    // A blocked localStorage only costs the remembered branch, never the run.
  }
};

// Architectural summary: polls status while visible; start/stop sit behind a ref lock (Rule 44).
export const useE2eRunnerController = (isActive: boolean): E2eRunnerController => {
  const [status, setStatus] = useState<E2eRunnerStatusView | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingTier, setPendingTier] = useState<E2eRunTier | null>(null);
  const [branchDraft, setBranchDraft] = useState('');
  const [areReportsOpen, setAreReportsOpen] = useState(false);
  const [runs, setRuns] = useState<E2eRunReportView[]>([]);
  const [isRunsLoading, setIsRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const runsInFlightRef = useRef(false);
  const [statusError, setStatusError] = useState(false);
  const inFlightRef = useRef(false);
  const pollingRef = useRef(false);
  const failureCountRef = useRef(0);

  const refresh = useCallback(async () => {
    if (pollingRef.current) {
      return;
    }
    pollingRef.current = true;
    try {
      const next = await e2eRunnerService.getStatus();
      failureCountRef.current = 0;
      setStatus(next);
      setError(null);
      setStatusError(false);
    } catch {
      failureCountRef.current += 1;
      setStatusError(failureCountRef.current >= MAX_FAILURES_BEFORE_BACKOFF);
    } finally {
      pollingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    // Clearing first keeps a visibilitychange from starting a second parallel poll chain.
    const tick = async () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (stopped || document.visibilityState !== 'visible') {
        return;
      }
      await refresh();
      if (stopped) {
        return;
      }
      const delay = failureCountRef.current >= MAX_FAILURES_BEFORE_BACKOFF ? E2E_POLL_BACKOFF_MS : E2E_POLL_INTERVAL_MS;
      timer = setTimeout(() => {
        void tick();
      }, delay);
    };

    void tick();
    document.addEventListener('visibilitychange', tick);

    return () => {
      stopped = true;
      if (timer) {
        clearTimeout(timer);
      }
      document.removeEventListener('visibilitychange', tick);
    };
  }, [isActive, refresh]);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsBusy(true);
      try {
        await action();
        setError(null);
        await refresh();
      } catch (actionError: unknown) {
        setError(actionError instanceof Error ? actionError.message : 'הפעולה נכשלה');
      } finally {
        inFlightRef.current = false;
        setIsBusy(false);
      }
    },
    [refresh],
  );

  // The remembered branch wins over the deployment default so a re-run repeats the last choice.
  const requestLaunch = useCallback(
    (tier: E2eRunTier) => {
      setBranchDraft(readStoredBranch() || status?.branch || '');
      setPendingTier(tier);
    },
    [status?.branch],
  );

  const cancelLaunch = useCallback(() => {
    setPendingTier(null);
  }, []);

  const isBranchDraftValid = E2E_RUN_BRANCH_PATTERN.test(branchDraft);

  const confirmLaunch = useCallback(() => {
    if (!pendingTier || !E2E_RUN_BRANCH_PATTERN.test(branchDraft)) {
      return;
    }
    const tier = pendingTier;
    const branch = branchDraft;
    setPendingTier(null);
    storeBranch(branch);
    void run(() => e2eRunnerService.start(tier, branch));
  }, [branchDraft, pendingTier, run]);

  // Report URLs are presigned and short-lived, so the list loads on open, never on a poll.
  const toggleReports = useCallback(() => {
    if (areReportsOpen) {
      setAreReportsOpen(false);
      return;
    }
    setAreReportsOpen(true);
    if (runsInFlightRef.current) {
      return;
    }
    runsInFlightRef.current = true;
    setIsRunsLoading(true);
    void e2eRunnerService
      .listRuns()
      .then((next) => {
        setRuns(next);
        setRunsError(null);
      })
      .catch((listError: unknown) => {
        setRunsError(listError instanceof Error ? listError.message : 'טעינת דוחות הבדיקות נכשלה');
      })
      .finally(() => {
        runsInFlightRef.current = false;
        setIsRunsLoading(false);
      });
  }, [areReportsOpen]);

  const stopRun = useCallback(() => {
    void run(() => e2eRunnerService.stop());
  }, [run]);

  return {
    status,
    isBusy,
    error,
    statusError,
    pendingTier,
    branchDraft,
    isBranchDraftValid,
    areReportsOpen,
    runs,
    isRunsLoading,
    runsError,
    toggleReports,
    setBranchDraft,
    requestLaunch,
    cancelLaunch,
    confirmLaunch,
    stopRun,
  };
};
